import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Empty } from "@/components/ui-kit";
import {
  Building2,
  Users,
  CheckCircle2,
  IndianRupee,
  Landmark,
  ReceiptText,
  Clock,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

const previousMonth = () => {
  const now = new Date();

  const d = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    1
  );

  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
  ].join("-");
};

const prettyMonth = (value) => {
  if (!value) return "—";

  const [y, m] = value.split("-").map(Number);

  return new Date(y, m - 1, 1).toLocaleDateString(
    "en-IN",
    {
      month: "long",
      year: "numeric",
    }
  );
};

const CARD_DEFS = [
  {
    key: "active_clients",
    label: "Active Clients",
    icon: Building2,
    money: false,
    route: "/clients",
  },
  {
    key: "active_employees",
    label: "Active Employees",
    icon: Users,
    money: false,
    route: "/employees",
  },
  {
    key: "inactive_employees",
    label: "Inactive Employees",
    icon: AlertTriangle,
    money: false,
    route: "/employees",
  },
  {
    key: "payroll_processed",
    label: "Payroll Processed",
    icon: CheckCircle2,
    money: false,
    route: "/attendance",
  },
  {
    key: "net_salary",
    label: "Net Salary",
    icon: IndianRupee,
    money: true,
    route: "/attendance",
  },
  {
    key: "employer_contributions",
    label: "Employer Contributions",
    icon: Landmark,
    money: true,
    route: "/reports",
  },
  {
    key: "invoice_amount",
    label: "Invoice Amount",
    icon: ReceiptText,
    money: true,
    route: "/invoices",
  },
  {
    key: "pending_amount",
    label: "Pending Amount",
    icon: IndianRupee,
    money: true,
    route: "/receivables",
  },
  {
    key: "pending_payroll",
    label: "Pending Payroll",
    icon: Clock,
    money: false,
    route: "/attendance",
  },
  {
    key: "pending_invoices",
    label: "Pending Invoices",
    icon: AlertTriangle,
    money: false,
    route: "/invoices",
  },
  {
    key: "gross_wages",
    label: "Gross Wages",
    icon: IndianRupee,
    money: true,
    route: "/analytics",
  },
  {
    key: "service_charge",
    label: "Service Charge",
    icon: ReceiptText,
    money: true,
    route: "/analytics",
  },
  {
    key: "gst",
    label: "GST Amount",
    icon: Landmark,
    money: true,
    route: "/invoices",
  },
];

const inr = (v) =>
  "₹" +
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });


const KPI_STYLES = {
  active_clients: {
    card:
      "nk-kpi-blue",
    icon:
      "bg-blue-100 text-blue-700",
    note:
      "Total active clients",
  },

  active_employees: {
    card:
      "nk-kpi-emerald",
    icon:
      "bg-emerald-100 text-emerald-700",
    note:
      "Current active workforce",
  },

  inactive_employees: {
    card:
      "nk-kpi-rose",
    icon:
      "bg-rose-100 text-rose-700",
    note:
      "Requires attention",
  },

  payroll_processed: {
    card:
      "nk-kpi-violet",
    icon:
      "bg-violet-100 text-violet-700",
    note:
      "Processed payroll runs",
  },

  net_salary: {
    card:
      "nk-kpi-amber",
    icon:
      "bg-amber-100 text-amber-700",
    note:
      "Net salary payable",
  },

  employer_contributions: {
    card:
      "nk-kpi-cyan",
    icon:
      "bg-cyan-100 text-cyan-700",
    note:
      "PF + ESI contributions",
  },

  invoice_amount: {
    card:
      "nk-kpi-pink",
    icon:
      "bg-pink-100 text-pink-700",
    note:
      "Client invoice value",
  },

  pending_amount: {
    card:
      "nk-kpi-rose",
    icon:
      "bg-rose-100 text-rose-700",
    note:
      "Invoice amount pending collection",
  },

  pending_payroll: {
    card:
      "nk-kpi-orange",
    icon:
      "bg-orange-100 text-orange-700",
    note:
      "Payroll still pending",
  },

  pending_invoices: {
    card:
      "nk-kpi-indigo",
    icon:
      "bg-indigo-100 text-indigo-700",
    note:
      "Invoices requiring action",
  },

  gross_wages: {
    card:
      "nk-kpi-blue",
    icon:
      "bg-blue-100 text-blue-700",
    note:
      "Total earned wages",
  },

  service_charge: {
    card:
      "nk-kpi-emerald",
    icon:
      "bg-emerald-100 text-emerald-700",
    note:
      "Payroll service charge",
  },

  gst: {
    card:
      "nk-kpi-violet",
    icon:
      "bg-violet-100 text-violet-700",
    note:
      "GST on client billing",
  },
};

export default function Dashboard() {
  const { selectedClient } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [month, setMonth] = useState(previousMonth());
  const [activeKpi, setActiveKpi] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard", {
        params: {
          client_id: selectedClient,
          month,
        },
      })
      .then((r) => setData(r.data))
      .catch(() => {});

    api
      .get("/analytics", {
        params: {
          client_id: selectedClient,
          month,
        },
      })
      .then((r) =>
        setAnalyticsData(r.data)
      )
      .catch(() => {});
  }, [selectedClient, month]);

  const c = {
    ...(data?.cards || {}),
    gross_wages:
      analyticsData?.cards?.gross_wages ?? 0,
    service_charge:
      analyticsData?.cards?.service_charge ?? 0,
    gst:
      analyticsData?.cards?.gst ?? 0,

    pending_amount:
      analyticsData?.cards?.outstanding ?? 0,
  };

  const ach =
    analyticsData?.charts || {};

  const go = (card) => {
    const clientParam =
      selectedClient &&
      selectedClient !== "all"
        ? `&client_id=${encodeURIComponent(selectedClient)}`
        : "";

    switch (card.key) {
      case "active_clients":
        navigate("/clients");
        return;

      case "active_employees":
        navigate("/employees");
        return;

      case "inactive_employees":
        navigate(
          `/employees?inactivity_month=${encodeURIComponent(month)}&inactivity=1${clientParam}`
        );
        return;

      case "payroll_processed":
        navigate(
          `/payroll?month=${encodeURIComponent(month)}&status=processed${clientParam}`
        );
        return;

      case "net_salary":
        navigate(
          `/payroll?month=${encodeURIComponent(month)}&status=processed&view=net_salary${clientParam}`
        );
        return;

      case "employer_contributions":
        navigate(
          `/reports?month=${encodeURIComponent(month)}&report=employer_contribution${clientParam}`
        );
        return;

      case "invoice_amount":
        navigate(
          `/invoices?month=${encodeURIComponent(month)}${clientParam}`
        );
        return;

      case "pending_amount":
        navigate(
          `/receivables?month=${encodeURIComponent(month)}${clientParam}`
        );
        return;

      case "pending_payroll":
        navigate(
          `/attendance?month=${encodeURIComponent(month)}${clientParam}`
        );
        return;

      case "pending_invoices":
        navigate(
          `/invoices?month=${encodeURIComponent(month)}&status=pending${clientParam}`
        );
        return;

      case "gross_wages":
        navigate(
          `/analytics?month=${encodeURIComponent(month)}${clientParam}`
        );
        return;

      case "service_charge":
        navigate(
          `/analytics?month=${encodeURIComponent(month)}${clientParam}`
        );
        return;

      case "gst":
        navigate(
          `/invoices?month=${encodeURIComponent(month)}${clientParam}`
        );
        return;

      default:
        navigate(card.route);
    }
  };

  const drilldown =
    activeKpi
      ? data?.drilldowns?.[activeKpi]
      : null;

  const drillRows =
    drilldown?.rows || [];

  const activeCard =
    CARD_DEFS.find(
      (x) => x.key === activeKpi
    );

  return (
    <div>
      <div className="nk-dashboard-hero">

        <div>
          <div className="text-xs font-medium text-blue-600 mb-1">
            Welcome back
          </div>

          <h1 className="nk-dashboard-title">
            Payroll Dashboard
          </h1>

          <p className="nk-dashboard-subtitle">
            Monitor payroll, workforce, contributions and billing from one place.
          </p>
        </div>


        <div className="nk-dashboard-period">

          <div className="nk-overline mb-1.5">
            Payroll Month
          </div>

          <input
            type="month"
            value={month}
            onChange={(e) =>
              setMonth(
                e.target.value
              )
            }
            className="nk-dashboard-month"
            data-testid="dashboard-month"
          />

          <div className="text-[11px] text-slate-500 mt-1.5">
            {prettyMonth(month)} salary is normally paid in{" "}
            <span className="font-semibold text-slate-700">
              {prettyMonth(
                data?.payment_month
              )}
            </span>
          </div>

        </div>

      </div>

      <div className="nk-kpi-grid">

        {CARD_DEFS.map(
          (d) => {
            const Icon =
              d.icon;

            const style =
              KPI_STYLES[d.key] ||
              KPI_STYLES.active_clients;

            const isAlert =
              d.key ===
                "inactive_employees" ||
              d.key ===
                "pending_payroll" ||
              d.key ===
                "pending_invoices";

            return (
              <button
                type="button"
                key={d.key}
                className={
                  `nk-kpi-card ${style.card}`
                }
                data-testid={`card-${d.key}`}
                onClick={() =>
                  go(d)
                }
              >

                <div
                  className={
                    `nk-kpi-icon ${style.icon}`
                  }
                >
                  <Icon size={18} />
                </div>


                <div className="nk-kpi-content">

                  <div className="nk-kpi-label">
                    {d.label}
                  </div>

                  <div
                    className={
                      `nk-kpi-value ${
                        isAlert &&
                        Number(
                          c[d.key] || 0
                        ) > 0
                          ? "text-rose-700"
                          : ""
                      }`
                    }
                  >
                    {d.money
                      ? inr(
                          c[d.key]
                        )
                      : (
                          c[d.key] ??
                          "—"
                        )}
                  </div>

                  <div className="nk-kpi-note">
                    {style.note}
                  </div>

                </div>


                <ChevronRight
                  size={16}
                  className="nk-kpi-arrow"
                />

              </button>
            );
          }
        )}

      </div>

      <div className="nk-dashboard-analytics-grid">

        <Card className="nk-analytics-panel">

          <div className="nk-panel-header">
            <div>
              <div className="nk-panel-title">
                Payroll Status
              </div>

              <div className="nk-panel-subtitle">
                Payroll run distribution for the selected period
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/analytics")
              }
              className="nk-panel-link"
            >
              Details
            </button>
          </div>


          {(ach.payroll_status || []).length ? (
            <div className="nk-dashboard-chart nk-dashboard-donut">

              <ResponsiveContainer>

                <PieChart>

                  <Pie
                    data={
                      ach.payroll_status
                    }
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="44%"
                    innerRadius={52}
                    outerRadius={78}
                    paddingAngle={3}
                  >

                    {(ach.payroll_status || []).map(
                      (
                        item,
                        index
                      ) => (
                        <Cell
                          key={index}
                          fill={
                            [
                              "#2563eb",
                              "#10b981",
                              "#f59e0b",
                              "#8b5cf6",
                              "#ef4444",
                              "#94a3b8",
                            ][
                              index % 6
                            ]
                          }
                        />
                      )
                    )}

                  </Pie>

                  <Tooltip />

                  <Legend
                    verticalAlign="bottom"
                    wrapperStyle={{
                      fontSize: 10,
                    }}
                  />

                </PieChart>

              </ResponsiveContainer>

            </div>
          ) : (
            <div className="nk-chart-empty">
              No payroll status data
            </div>
          )}

        </Card>


        <Card className="nk-analytics-panel">

          <div className="nk-panel-header">

            <div>
              <div className="nk-panel-title">
                Payroll vs Client Billing
              </div>

              <div className="nk-panel-subtitle">
                Monthly net salary and billing trend
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/analytics")
              }
              className="nk-panel-link"
            >
              Analytics
            </button>

          </div>


          {(ach.payroll_vs_billing || []).length ? (
            <div className="nk-dashboard-chart">

              <ResponsiveContainer>

                <LineChart
                  data={
                    ach.payroll_vs_billing
                  }
                  margin={{
                    top: 8,
                    right: 8,
                    bottom: 0,
                    left: 0,
                  }}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e8edf5"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="month"
                    fontSize={9}
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    fontSize={9}
                    tickLine={false}
                    axisLine={false}
                    width={46}
                    tickFormatter={(v) =>
                      Number(v) >= 100000
                        ? `${(
                            Number(v) /
                            100000
                          ).toFixed(0)}L`
                        : v
                    }
                  />

                  <Tooltip
                    formatter={(v) =>
                      inr(v)
                    }
                  />

                  <Legend
                    wrapperStyle={{
                      fontSize: 10,
                    }}
                  />

                  <Line
                    type="monotone"
                    dataKey="payroll"
                    name="Net Salary"
                    stroke="#2563eb"
                    strokeWidth={2.4}
                    dot={{ r: 3 }}
                  />

                  <Line
                    type="monotone"
                    dataKey="billing"
                    name="Client Billing"
                    stroke="#10b981"
                    strokeWidth={2.4}
                    dot={{ r: 3 }}
                  />

                </LineChart>

              </ResponsiveContainer>

            </div>
          ) : (
            <div className="nk-chart-empty">
              No trend data
            </div>
          )}

        </Card>


        <Card className="nk-analytics-panel">

          <div className="nk-panel-header">

            <div>
              <div className="nk-panel-title">
                Client Payroll Cost
              </div>

              <div className="nk-panel-subtitle">
                Payroll cost comparison by client
              </div>
            </div>

          </div>


          {(ach.client_cost || []).length ? (
            <div className="nk-dashboard-chart">

              <ResponsiveContainer>

                <BarChart
                  data={
                    ach.client_cost
                  }
                  margin={{
                    top: 8,
                    right: 5,
                    bottom: 0,
                    left: 0,
                  }}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e8edf5"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="name"
                    fontSize={8}
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    fontSize={9}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    tickFormatter={(v) =>
                      Number(v) >= 100000
                        ? `${(
                            Number(v) /
                            100000
                          ).toFixed(0)}L`
                        : v
                    }
                  />

                  <Tooltip
                    formatter={(v) =>
                      inr(v)
                    }
                  />

                  <Bar
                    dataKey="value"
                    name="Client Cost"
                    fill="#3b82f6"
                    radius={[
                      6,
                      6,
                      2,
                      2,
                    ]}
                    maxBarSize={42}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>
          ) : (
            <div className="nk-chart-empty">
              No client cost data
            </div>
          )}

        </Card>

      </div>


      {activeKpi && (
        <Card className="overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="nk-head font-semibold text-sm">
                {activeCard?.label} — {prettyMonth(month)}
              </div>

              <div className="text-xs text-slate-500 mt-1">
                Showing the exact records contributing to this dashboard KPI
              </div>
            </div>

            <button
              onClick={() => setActiveKpi(null)}
              className="text-sm text-slate-500 hover:text-slate-800 px-2 py-1"
            >
              Close
            </button>
          </div>

          <div className="overflow-x-auto">
            {drillRows.length ? (
              <>
                {[
                  "payroll_processed",
                  "net_salary",
                  "employer_contributions",
                ].includes(activeKpi) && (
                  <table className="nk-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Employees</th>
                        <th>Status</th>

                        {activeKpi === "net_salary" && (
                          <th>Net Salary</th>
                        )}

                        {activeKpi ===
                          "employer_contributions" && (
                          <>
                            <th>Employer PF</th>
                            <th>Employer ESI</th>
                            <th>Total Contribution</th>
                          </>
                        )}

                        {activeKpi ===
                          "payroll_processed" && (
                          <th>Net Salary</th>
                        )}

                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {drillRows.map((r) => (
                        <tr key={r.run_id}>
                          <td className="font-medium">
                            {r.client}
                          </td>

                          <td className="nk-num">
                            {r.employees}
                          </td>

                          <td>
                            <StatusBadge
                              status={r.status}
                            />
                          </td>

                          {activeKpi ===
                            "net_salary" && (
                            <td className="nk-num">
                              ₹{inr(
                                r.net_salary
                              ).replace("₹", "")}
                            </td>
                          )}

                          {activeKpi ===
                            "employer_contributions" && (
                            <>
                              <td className="nk-num">
                                ₹{inr(
                                  r.employer_pf
                                ).replace("₹", "")}
                              </td>

                              <td className="nk-num">
                                ₹{inr(
                                  r.employer_esi
                                ).replace("₹", "")}
                              </td>

                              <td className="nk-num font-semibold">
                                ₹{inr(
                                  r.employer_contributions
                                ).replace("₹", "")}
                              </td>
                            </>
                          )}

                          {activeKpi ===
                            "payroll_processed" && (
                            <td className="nk-num">
                              ₹{inr(
                                r.net_salary
                              ).replace("₹", "")}
                            </td>
                          )}

                          <td className="text-right">
                            <button
                              onClick={() =>
                                navigate(
                                  `/payroll?run_id=${encodeURIComponent(
                                    r.run_id
                                  )}`
                                )
                              }
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Open Payroll
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    {(activeKpi === "net_salary" ||
                      activeKpi ===
                        "employer_contributions") && (
                      <tfoot>
                        <tr className="font-bold bg-slate-50">
                          <td>TOTAL</td>
                          <td></td>
                          <td></td>

                          {activeKpi ===
                            "net_salary" && (
                            <td className="nk-num">
                              {inr(
                                drilldown?.total
                              )}
                            </td>
                          )}

                          {activeKpi ===
                            "employer_contributions" && (
                            <>
                              <td></td>
                              <td></td>
                              <td className="nk-num">
                                {inr(
                                  drilldown?.total
                                )}
                              </td>
                            </>
                          )}

                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}

                {activeKpi ===
                  "inactive_employees" && (
                  <table className="nk-table min-w-[900px]">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>UAN</th>
                        <th>Client</th>
                        <th>Designation</th>
                        <th>Month</th>
                        <th>Reason</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {drillRows.map((r) => (
                        <tr key={r.employee_id}>
                          <td className="font-semibold text-red-700">
                            {r.name || "—"}
                          </td>

                          <td className="nk-num">
                            {r.uan || "—"}
                          </td>

                          <td>
                            {r.client_name || "—"}
                          </td>

                          <td>
                            {r.designation || "—"}
                          </td>

                          <td>
                            {r.month || month}
                          </td>

                          <td>
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2 py-1 text-xs font-semibold text-red-700">
                              <AlertTriangle size={12} />
                              {r.reason || "No working-day activity"}
                            </span>
                          </td>

                          <td className="text-right">
                            <button
                              onClick={() =>
                                navigate(
                                  `/employees/${r.employee_id}`
                                )
                              }
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Open Employee
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    <tfoot>
                      <tr className="font-bold bg-red-50">
                        <td>FLAGGED EMPLOYEES</td>
                        <td className="nk-num">
                          {drilldown?.total || 0}
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}

                {activeKpi ===
                  "pending_payroll" && (
                  <table className="nk-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Active Employees</th>
                        <th>Month</th>
                        <th>Current Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {drillRows.map((r) => (
                        <tr key={r.client_id}>
                          <td className="font-medium">
                            {r.client}
                          </td>

                          <td className="nk-num">
                            {r.active_employees}
                          </td>

                          <td className="nk-num">
                            {r.month}
                          </td>

                          <td>
                            <StatusBadge
                              status={
                                r.existing_status ===
                                "not_started"
                                  ? "draft"
                                  : r.existing_status
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    <tfoot>
                      <tr className="font-bold bg-slate-50">
                        <td>
                          TOTAL PENDING
                        </td>
                        <td className="nk-num">
                          {drilldown?.total}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}

                {[
                  "invoice_amount",
                  "pending_invoices",
                ].includes(activeKpi) && (
                  <table className="nk-table">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Client</th>
                        <th>Status</th>
                        <th>Payroll Cost</th>
                        <th>Service Charge</th>
                        <th>GST</th>
                        <th>Grand Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {drillRows.map((r) => (
                        <tr key={r.invoice_id}>
                          <td className="font-medium">
                            {r.number}
                          </td>

                          <td>
                            {r.client}
                          </td>

                          <td>
                            <StatusBadge
                              status={r.status}
                            />
                          </td>

                          <td className="nk-num">
                            {inr(r.payroll_cost)}
                          </td>

                          <td className="nk-num">
                            {inr(r.margin)}
                          </td>

                          <td className="nk-num">
                            {inr(r.gst)}
                          </td>

                          <td className="nk-num font-semibold">
                            {inr(r.grand_total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    {activeKpi ===
                      "invoice_amount" && (
                      <tfoot>
                        <tr className="font-bold bg-slate-50">
                          <td>TOTAL</td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td className="nk-num">
                            {inr(
                              drilldown?.total
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}
              </>
            ) : (
              <Empty
                title={`No ${activeCard?.label || "records"}`}
                hint={`No matching records for ${prettyMonth(
                  month
                )}.`}
              />
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="xl:col-span-2 overflow-hidden nk-dashboard-panel">
          <div className="px-4 py-3 border-b border-slate-200 nk-head font-semibold text-sm flex items-center justify-between">
            <span>
              Payroll Status — {prettyMonth(month)}
            </span>

            <span className="text-xs text-slate-400 font-normal">
              {data?.status_table?.length || 0} run(s)
            </span>
          </div>

          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            {data?.status_table?.length ? (
              <table
                className="nk-table"
                data-testid="payroll-status-table"
              >
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Employees</th>
                    <th>Month</th>
                    <th>Payroll</th>
                    <th>Invoice</th>
                  </tr>
                </thead>

                <tbody>
                  {data.status_table.map((r, i) => (
                    <tr
                      key={r.run_id || i}
                      className="cursor-pointer hover:bg-blue-50"
                      onClick={() =>
                        navigate(
                          `/attendance?month=${encodeURIComponent(
                            r.month || month
                          )}&client_id=${encodeURIComponent(
                            r.client_id || ""
                          )}`
                        )
                      }
                    >
                      <td className="font-medium text-slate-800">
                        {r.client}
                      </td>

                      <td className="nk-num">
                        {r.employees}
                      </td>

                      <td className="nk-num">
                        {r.month}
                      </td>

                      <td>
                        <StatusBadge
                          status={r.payroll_status}
                        />
                      </td>

                      <td>
                        <StatusBadge
                          status={r.invoice_status}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Empty
                title={`No payroll runs for ${prettyMonth(month)}`}
                hint="Open Attendance & Inputs to start payroll."
              />
            )}
          </div>
        </Card>

        <Card className="overflow-hidden nk-dashboard-panel">
          <div className="px-4 py-3 border-b border-slate-200 nk-head font-semibold text-sm flex items-center gap-2">
            <AlertTriangle
              size={15}
              className="text-amber-500"
            />
            Needs Attention — {prettyMonth(month)}
          </div>

          <div
            className="p-3 space-y-2 max-h-[420px] overflow-y-auto"
            data-testid="attention-panel"
          >
            {data?.attention?.length ? (
              data.attention.map((a, i) => (
                <div
                  key={i}
                  className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 cursor-pointer hover:bg-amber-100"
                  onClick={() => {
                    const params = new URLSearchParams();

                    params.set(
                      "month",
                      a.month || month
                    );

                    if (a.client_id) {
                      params.set(
                        "client_id",
                        a.client_id
                      );
                    }

                    navigate(
                      `/attendance?${params.toString()}`
                    );
                  }}
                >
                  {a.message}
                </div>
              ))
            ) : (
              <Empty
                title="All clear"
                hint={`Nothing needs attention for ${prettyMonth(month)}.`}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
